import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { deleteField } from 'firebase/firestore';
import { SweepsFirestore } from '../../../services/sweepsFirestore';
import { useTheme } from '../../../contexts/ThemeContext';
import { normalizeSweepScheduledDateInput } from '../../../utils/sweepSchedule';
import type { SweepFolder } from '../../../types/sweep';

export interface AddSweepLocationDialogProps {
  open: boolean;
  onClose: () => void;
  masterOwnerID: string;
  ownerID?: string;
  /** Sweep folders for this tenant (optional folder assignment). */
  folders?: SweepFolder[];
  /** When adding a new stop, pre-select this folder if present in `folders`. */
  defaultSweepFolderId?: string | null;
  editing?: {
    id: string;
    name?: string;
    address?: string;
    knownInfo?: string;
    readerCount?: number;
    stopSequence?: number;
    teamNumber?: number | null;
    sweepScheduledDate?: string | null;
    sweepFolderId?: string | null;
  } | null;
  onSaved?: (id: string) => void;
}

const AddSweepLocationDialog: React.FC<AddSweepLocationDialogProps> = ({
  open,
  onClose,
  masterOwnerID,
  ownerID,
  folders = [],
  defaultSweepFolderId,
  editing,
  onSaved,
}) => {
  const { colors } = useTheme();
  const isEditing = !!editing?.id;
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [knownInfo, setKnownInfo] = useState('');
  const [readerCount, setReaderCount] = useState('');
  const [stopSequence, setStopSequence] = useState('');
  const [teamNumberStr, setTeamNumberStr] = useState('');
  const [scheduledYmd, setScheduledYmd] = useState('');
  const [folderId, setFolderId] = useState<string>('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(String(editing?.name || '').trim());
    setAddress(String(editing?.address || '').trim());
    setKnownInfo(String(editing?.knownInfo || '').trim());
    setReaderCount(
      typeof editing?.readerCount === 'number' && Number.isFinite(editing.readerCount)
        ? String(editing.readerCount)
        : ''
    );
    setStopSequence(
      typeof editing?.stopSequence === 'number' && Number.isFinite(editing.stopSequence)
        ? String(editing.stopSequence)
        : ''
    );
    setTeamNumberStr(
      typeof editing?.teamNumber === 'number' && Number.isFinite(editing.teamNumber)
        ? String(Math.round(editing.teamNumber))
        : ''
    );
    setScheduledYmd(
      String(editing?.sweepScheduledDate ?? '')
        .trim()
        .slice(0, 10) || ''
    );
    if (editing?.id) {
      setFolderId(String(editing?.sweepFolderId ?? '').trim());
    } else {
      const def = String(defaultSweepFolderId ?? '').trim();
      setFolderId(def && folders.some((f) => f.id === def) ? def : '');
    }
    setError('');
  }, [
    open,
    editing?.id,
    editing?.name,
    editing?.address,
    editing?.knownInfo,
    editing?.readerCount,
    editing?.stopSequence,
    editing?.teamNumber,
    editing?.sweepScheduledDate,
    editing?.sweepFolderId,
    defaultSweepFolderId,
    folders,
  ]);

  const parsedReaderCount = useMemo(() => {
    const raw = String(readerCount || '').trim();
    if (!raw) return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return Math.round(n);
  }, [readerCount]);

  const onSave = async () => {
    const nm = String(name || '').trim();
    const addr = String(address || '').trim();
    if (!nm) {
      setError('Please enter a name.');
      return;
    }
    if (!addr) {
      setError('Please enter an address.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const stopRaw = String(stopSequence || '').trim();
      const hadStop =
        isEditing && typeof editing?.stopSequence === 'number' && Number.isFinite(editing.stopSequence);
      let stopPatch: Record<string, unknown> = {};
      if (!stopRaw) {
        if (hadStop) stopPatch = { stopSequence: deleteField() };
      } else {
        const sn = Number(stopRaw);
        if (!Number.isFinite(sn)) {
          setError('Stop # must be a number.');
          setSaving(false);
          return;
        }
        stopPatch = { stopSequence: Math.round(sn) };
      }

      const schedTrim = String(scheduledYmd || '').trim();
      const schedNorm = schedTrim ? normalizeSweepScheduledDateInput(schedTrim) : undefined;
      if (schedTrim && !schedNorm) {
        setError('Visit date must be YYYY-MM-DD (or clear the field).');
        setSaving(false);
        return;
      }
      const hadScheduled = isEditing && String(editing?.sweepScheduledDate ?? '').trim() !== '';
      let schedPatch: Record<string, unknown> = {};
      if (!schedNorm) {
        if (hadScheduled) schedPatch = { sweepScheduledDate: deleteField() };
      } else {
        schedPatch = { sweepScheduledDate: schedNorm };
      }

      const hadFolder = isEditing && String(editing?.sweepFolderId ?? '').trim() !== '';
      let folderPatch: Record<string, unknown> = {};
      const fid = String(folderId || '').trim();
      if (!fid) {
        if (hadFolder) folderPatch = { sweepFolderId: deleteField() };
      } else {
        folderPatch = { sweepFolderId: fid };
      }

      const teamRaw = String(teamNumberStr || '').trim();
      const hadTeam =
        isEditing && typeof editing?.teamNumber === 'number' && Number.isFinite(editing.teamNumber);
      let teamPatch: Record<string, unknown> = {};
      if (!teamRaw) {
        if (hadTeam) teamPatch = { teamNumber: deleteField() };
      } else {
        const tn = Number(teamRaw);
        if (!Number.isFinite(tn)) {
          setError('Team # must be a whole number.');
          setSaving(false);
          return;
        }
        teamPatch = { teamNumber: Math.round(tn) };
      }

      const id = await SweepsFirestore.upsertSweepLocation(
        { masterOwnerID, ownerID, id: editing?.id || undefined },
        {
          name: nm,
          address: addr,
          knownInfo: String(knownInfo || '').trim(),
          readerCount: parsedReaderCount,
          ...stopPatch,
          ...schedPatch,
          ...folderPatch,
          ...teamPatch,
        }
      );
      onSaved?.(id);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save sweep location.');
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      background: 'rgba(255,255,255,0.04)',
    },
    '& .MuiInputLabel-root': { color: colors.TextMuted },
    '& .MuiOutlinedInput-input': { color: colors.TextPrimary },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>
        {isEditing ? 'Edit sweep location' : 'Add sweep location'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={fieldSx} />
        <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth sx={fieldSx} />
        {folders.length ? (
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel id="sweep-folder-lbl" sx={{ color: colors.TextMuted }}>
              Sweep folder (optional)
            </InputLabel>
            <Select
              labelId="sweep-folder-lbl"
              label="Sweep folder (optional)"
              value={folderId || ''}
              onChange={(e) => setFolderId(String(e.target.value))}
              sx={{ color: colors.TextPrimary, borderRadius: '12px' }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name || f.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        <TextField
          label="Stop # (route order, optional)"
          value={stopSequence}
          onChange={(e) => setStopSequence(e.target.value)}
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="Team # (optional)"
          value={teamNumberStr}
          onChange={(e) => setTeamNumberStr(e.target.value.replace(/\D/g, ''))}
          fullWidth
          sx={fieldSx}
          helperText="Used for sweeps list, filters, and imports. Clear and save to remove."
        />
        <TextField
          label="Visit date for Report Site browse (optional)"
          type="date"
          value={scheduledYmd}
          onChange={(e) => setScheduledYmd(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText="Leave empty so this stop appears any day until checked off. When set, it only appears in browse on that calendar day."
          sx={fieldSx}
        />
        <TextField
          label="Reader count (optional)"
          value={readerCount}
          onChange={(e) => setReaderCount(e.target.value)}
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="Known information / notes (optional)"
          value={knownInfo}
          onChange={(e) => setKnownInfo(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          sx={fieldSx}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: colors.TextMuted }}>
          Cancel
        </Button>
        <Button
          onClick={() => void onSave()}
          disabled={saving}
          variant="outlined"
          sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
        >
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSweepLocationDialog;
